import { ResourceClient, type HttpClient, type JobDTO } from "@dl/web-core-js/api";

/** A product field whose values become Shopify tags. */
export type ShopifyTagSource = "CATEGORY" | "COLLECTION" | "COLOR" | "SIZE" | "KEYWORDS" | "PRODUCT_TAG" | "DESIGNER";

/** The three publications we manage by hand: the storefront and the two market catalogs. */
export type ShopifyPublicationTarget = "ONLINE_STORE" | "CA" | "US";

export interface ShopifyTagSourceDTO {
  id: number;
  source: ShopifyTagSource;
  enabled: boolean;
  sort_order: number;
}

/**
 * One Shopify image rule: up to `quantity` images of `image_type` (of `orientation` when set,
 * any when null) for a variant of `product_type`, in rule order.
 */
export interface ShopifyImageRuleDTO {
  id: number;
  product_type: "SINGLE_ITEM" | "BUNDLE";
  image_type: string;
  orientation: "FRONT" | "ANGLED" | "NA" | null;
  quantity: number;
  sort_order: number;
}

/** What nexpim remembers about a group's Shopify product: the gid, the opt-out and the default product. */
export interface ShopifyListingDTO {
  id: number;
  variation_group: number;
  product_gid?: string | null;
  excluded: boolean;
  /** The product the listing opens on (variant 1); null = SKU order. */
  default_product_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ShopifyProblemDTO {
  sku?: string | null;
  code: string;
  message: string;
  blocking: boolean;
}

export interface ShopifyPreviewDTO {
  variation_group: number;
  product_gid?: string | null;
  title: string;
  blocked: boolean;
  create: boolean;
  noop: boolean;
  product_set: boolean;
  changes: string[];
  tags: string[];
  tags_add: string[];
  tags_remove: string[];
  translations: boolean;
  problems: ShopifyProblemDTO[];
}

export interface ShopifyListingPublication {
  id: string;
  name: string;
  target?: ShopifyPublicationTarget | null;
  published: boolean;
}

export interface ShopifyListingVariant {
  product_id: number;
  sku: string;
  status?: string | null;
  excluded: boolean;
  option_values: string;
  price?: number | null;
  variant_gid?: string | null;
  inventory_item_gid?: string | null;
  image_url?: string | null;
  problem?: string | null;
}

export interface ShopifyListingMedia {
  id: string;
  alt?: string | null;
  url?: string | null;
  status?: string | null;
}

/** The Shopify tab: the group's product as Shopify holds it now, plus what a sync would change. */
export interface ShopifyListingViewDTO {
  variation_group: number;
  listed: boolean;
  excluded: boolean;
  product_gid?: string | null;
  legacy_id?: string | null;
  title?: string | null;
  handle?: string | null;
  status?: string | null;
  admin_url?: string | null;
  storefront_url?: string | null;
  /** Product URL per market it is published in (handle → URL); empty while off the Online Store. */
  storefront_urls: Record<string, string>;
  review_count?: number | null;
  has_french: boolean;
  tags: string[];
  publications: ShopifyListingPublication[];
  variants: ShopifyListingVariant[];
  media: ShopifyListingMedia[];
  preview: ShopifyPreviewDTO;
  /** The product the listing opens on (variant 1); null = SKU order. */
  default_product_id?: number | null;
}

export interface ShopifyListingSummaryDTO {
  product_id: number;
  variation_group: number;
  listed: boolean;
  group_excluded: boolean;
  product_excluded: boolean;
  on_shopify: boolean;
  product_gid?: string | null;
  status?: string | null;
  admin_url?: string | null;
  storefront_url?: string | null;
  online_store: boolean;
  market_ca: boolean;
  market_us: boolean;
}

export interface ShopifyStoreInfoDTO {
  shop_name: string;
  domain: string;
  admin_url: string;
  currency: string;
  presentment_currencies: string[];
  api_version: string;
  locales: Array<{ locale: string; name: string; primary: boolean; published: boolean }>;
  publications: Array<{ id: string; name: string }>;
  online_store_publication_id?: string | null;
  markets: Array<{
    id: string;
    name: string;
    handle: string;
    primary: boolean;
    enabled: boolean;
    currency: string;
    catalog_id: string;
    publication_id?: string | null;
    price_list_currency?: string | null;
    fixed_prices_count?: number | null;
    /** The market's storefront root (https://nexera.com/en-us/), null when it shares the primary domain. */
    root_url?: string | null;
  }>;
  location_id?: string | null;
  /** Every active location; a Shopify sales channel's external_location_id is the numeric tail of one of these. */
  locations: Array<{ id: string; name: string }>;
  access_scopes: string[];
  missing_scopes: string[];
  discovered_at: string;
}

/** The admin URL of a product from its gid alone: the numeric tail is the legacy id. */
export function shopifyAdminProductUrl(adminUrl: string, productGid: string): string {
  return `${adminUrl}/products/${productGid.substring(productGid.lastIndexOf("/") + 1)}`;
}

export class ShopifyTagSourceClient extends ResourceClient<ShopifyTagSourceDTO> {
  constructor(http: HttpClient) {
    super(http, "/shopify/tag-source");
  }
}

/**
 * The product sync. Everything is addressed by nexpim product id; the server
 * resolves the variation group. Long calls return the job to watch.
 */
export class ShopifyClient {
  constructor(private readonly http: HttpClient) {}

  store(): Promise<ShopifyStoreInfoDTO> {
    return this.http.get<ShopifyStoreInfoDTO>("/shopify/store/");
  }

  refreshStore(): Promise<ShopifyStoreInfoDTO> {
    return this.http.post<ShopifyStoreInfoDTO>("/shopify/store/refresh");
  }

  /** What nexpim remembers per group — no Shopify call; the grid column reads this. */
  listed(): Promise<ShopifyListingDTO[]> {
    return this.http.get<ShopifyListingDTO[]>("/shopify/listing/listed");
  }

  view(productId: number): Promise<ShopifyListingViewDTO> {
    return this.http.get<ShopifyListingViewDTO>(`/shopify/listing/product/${productId}`);
  }

  preview(productId: number): Promise<ShopifyPreviewDTO> {
    return this.http.get<ShopifyPreviewDTO>(`/shopify/listing/product/${productId}/preview`);
  }

  summaries(productIds: number[]): Promise<Record<number, ShopifyListingSummaryDTO>> {
    return this.http.post<Record<number, ShopifyListingSummaryDTO>>("/shopify/listing/summaries", productIds);
  }

  sync(productIds: number[]): Promise<JobDTO> {
    return this.http.post<JobDTO>("/shopify/listing/sync", productIds);
  }

  syncAll(): Promise<JobDTO> {
    return this.http.post<JobDTO>("/shopify/listing/sync-all");
  }

  match(): Promise<JobDTO> {
    return this.http.post<JobDTO>("/shopify/listing/match");
  }

  /** Gives every listed product the handle its title reads as, redirecting the old URL; dryRun only lists them. */
  cleanupHandles(dryRun: boolean): Promise<JobDTO> {
    return this.http.post<JobDTO>("/shopify/listing/cleanup-handles", null, { query: { dry_run: String(dryRun) } });
  }

  /** Archives the store products the match left without a group; dryRun only lists them in the job. */
  archiveUnlinked(dryRun: boolean): Promise<JobDTO> {
    return this.http.post<JobDTO>("/shopify/listing/archive-unlinked", null, { query: { dry_run: String(dryRun) } });
  }

  setPublished(productIds: number[], target: ShopifyPublicationTarget, published: boolean): Promise<number[]> {
    return this.http.post<number[]>(`/shopify/listing/publication/${target}/${published}`, productIds);
  }

  archive(productIds: number[]): Promise<number[]> {
    return this.http.post<number[]>("/shopify/listing/archive", productIds);
  }

  remove(productIds: number[]): Promise<number[]> {
    return this.http.delete<number[]>("/shopify/listing/remote", { body: productIds });
  }

  setProductExcluded(productId: number, excluded: boolean): Promise<void> {
    return this.http.put<void>(`/shopify/listing/product/${productId}/excluded/${excluded}`);
  }

  setGroupExcluded(productId: number, excluded: boolean): Promise<ShopifyListingDTO> {
    return this.http.put<ShopifyListingDTO>(`/shopify/listing/product/${productId}/group-excluded/${excluded}`);
  }

  /** The product the group's listing opens on; null goes back to SKU order. */
  setDefaultProduct(productId: number, defaultProductId: number | null): Promise<ShopifyListingDTO> {
    return this.http.put<ShopifyListingDTO>(`/shopify/listing/product/${productId}/default`, null, {
      query: defaultProductId == null ? {} : { default_product_id: String(defaultProductId) },
    });
  }

  unlink(productId: number): Promise<void> {
    return this.http.delete<void>(`/shopify/listing/product/${productId}/link`);
  }
}
